import React from 'react'
import { isEmpty } from 'lodash'

import { Table, PreviewImage, Tag, Skeleton } from 'components'
import { THead, TBody, Row, ActionEventTable } from 'components/Table'
import { setValue, setFormatDate } from 'helpers/common'

import type { IProgressTable, sourceImage } from '../model'
import type { IColumn } from 'types'
import { setProgressLogContent } from './TicketProgressTableContent'

function TicketProgressTable(props: IProgressTable): JSX.Element {
  const { isLoading, isError, columns, progressLogs, getProductValue } = props

  const base = 'f-ticket-progress-info'
  const skeletonHeight = '18px'

  let loadingList: Record<string, React.ReactNode>[] = []

  const renderPreviewImages = (list: sourceImage, text: string) => (
    <div
      className={`${base}__detail-cell`}
      data-testid='progress-info-detail-cell'
    >
      <PreviewImage list={list} />
      <p className={`${base}__detail-cell--text`}>{text}</p>
    </div>
  )

  const renderStatus = (status: string) => {
    return status ? (
      <div className='con-ticket__cell-body-wrapper'>
        <span className='con-ticket__cell-body-wrapper'>
          <Tag status={status} />
        </span>
      </div>
    ) : (
      '-'
    )
  }

  const renderColumnLoading = {
    createdAt: (
      <div className={`${base}__skeleton--layout-column`}>
        <Skeleton.whiteSkeleton height={skeletonHeight} width='100%' />
      </div>
    ),
    status: (
      <div className={`${base}__skeleton--layout-column`}>
        <Skeleton.whiteSkeleton height={skeletonHeight} width='100%' />
      </div>
    ),
    subject: (
      <div className={`${base}__skeleton--layout-column`}>
        <Skeleton.whiteSkeleton height={skeletonHeight} width='100%' />
        <Skeleton.whiteSkeleton height={skeletonHeight} width='80%' />
      </div>
    ),
    updatedByFullName: (
      <div className={`${base}__skeleton--layout-column`}>
        <Skeleton.whiteSkeleton height={skeletonHeight} width='100%' />
      </div>
    ),
    detail: (
      <div className={`${base}__skeleton--layout-row`}>
        <Skeleton.whiteSkeleton height='80px' width='80px' />
        <div className={`${base}__skeleton--layout-column`}>
          <Skeleton.whiteSkeleton height={skeletonHeight} width='90%' />
          <Skeleton.whiteSkeleton height={skeletonHeight} width='100%' />
          <Skeleton.whiteSkeleton height={skeletonHeight} width='60%' />
        </div>
      </div>
    ),
  }

  const list: Record<string, React.ReactNode>[] = progressLogs.map(
    (item, index) => {
      return {
        createdAt: setFormatDate(item.createdAt),
        updatedByFullName:
          item.updatedByFullName === 'fixtab-bff'
            ? 'Fixtab - Microsite'
            : setValue(item.updatedByFullName),
        ...setProgressLogContent(
          item.eventType,
          index,
          progressLogs,
          renderStatus,
          renderPreviewImages,
          getProductValue,
        ),
      }
    },
  )

  const dataTable = (columns: IColumn[]): JSX.Element => {
    if (isLoading) {
      loadingList = Array(3)
        .fill(0)
        .map(() => renderColumnLoading)
    }

    if ((!isLoading && isEmpty(progressLogs)) || isError) {
      return <ActionEventTable colSpan={columns.length} isNotFound />
    }

    return (
      <>
        {isLoading
          ? loadingList.map((data, index) => (
              <Row
                key={index}
                columns={columns}
                data={data}
                verticalAlign={isLoading ? 'baseline' : ''}
              />
            ))
          : list.map((data, index) => (
              <Row key={index} columns={columns} data={data} />
            ))}
      </>
    )
  }

  return (
    <Table className={`${base}__main-table`}>
      <THead columns={columns} />
      <TBody>{dataTable(columns)}</TBody>
    </Table>
  )
}

export default TicketProgressTable
